export const NONOGRAM_SIZES = [
  { width: 10, height: 10 },
  { width: 13, height: 9 },
  { width: 13, height: 12 },
  { width: 7, height: 15 },
  { width: 14, height: 15 },
] as const;

export type NonogramCell = 0 | 1 | 2;

export type NonogramPuzzle = {
  width: number;
  height: number;
  solution: boolean[];
  rowClues: number[][];
  columnClues: number[][];
  candidateId?: string;
  generatorFamily?: 'skeleton' | 'radial';
  symmetry?: 'bilateral' | 'rotational' | 'asymmetric';
  bitmapHash?: string;
  rasterization?: {
    scale: number;
    offsetX: number;
    offsetY: number;
    threshold: number;
    morph: -1 | 0 | 1;
  };
  repair?: 'none' | 'remove-isolated' | 'fill-hole' | 'remove-spur' | 'bridge-gap' | 'close';
  quality?: {
    visualScore: number;
    density: number;
    componentCount: number;
    boundingBoxUsage: number;
    clueVariety: number;
    negativeSpace: number;
    propagationRounds: number;
    openingDeductions: number;
    narrowDeductionSteps: number;
    maximumStallSize: number;
    rowDeductionCount: number;
    columnDeductionCount: number;
    difficulty: number;
  };
  spriteId?: string;
  transform?: 'identity' | 'horizontal' | 'vertical' | 'rotate-180';
};

function validClueSet(value: unknown, lineCount: number): value is number[][] {
  return (
    Array.isArray(value) &&
    value.length === lineCount &&
    value.every(
      (clues) =>
        Array.isArray(clues) &&
        clues.length > 0 &&
        clues.every((clue) => Number.isInteger(clue) && clue >= 0),
    )
  );
}

export function isNonogramPuzzle(value: unknown): value is NonogramPuzzle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<NonogramPuzzle>;
  const supportedSize = NONOGRAM_SIZES.some(
    ({ width, height }) => candidate.width === width && candidate.height === height,
  );
  return (
    supportedSize &&
    Array.isArray(candidate.solution) &&
    candidate.solution.length === candidate.width! * candidate.height! &&
    candidate.solution.every((cell) => typeof cell === 'boolean') &&
    candidate.solution.some(Boolean) &&
    candidate.solution.some((cell) => !cell) &&
    validClueSet(candidate.rowClues, candidate.height!) &&
    validClueSet(candidate.columnClues, candidate.width!) &&
    ((typeof candidate.candidateId === 'string' &&
      ['skeleton', 'radial'].includes(candidate.generatorFamily ?? '') &&
      ['bilateral', 'rotational', 'asymmetric'].includes(candidate.symmetry ?? '') &&
      typeof candidate.bitmapHash === 'string' &&
      Boolean(candidate.quality)) ||
      (typeof candidate.spriteId === 'string' &&
        ['identity', 'horizontal', 'vertical', 'rotate-180'].includes(candidate.transform ?? '')))
  );
}
